import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productsAPI, categoriesAPI, authAPI, cartAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Star, TrendingUp } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { hintText } from '@/lib/utils';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await authAPI.getMe();
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleAddToCart = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      await cartAPI.addToCart(productId, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const loadData = async () => {
    try {
      const [products, cats] = await Promise.all([
        productsAPI.getAll({ featured: true, limit: 4 }),
        categoriesAPI.getAll()
      ]);
      setFeaturedProducts(products);
      setCategories(cats);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1752223638233-4c9545333f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbGFwdG9wJTIwd29ya3NwYWNlJTIwd2hpdGUlMjBkZXNrfGVufDB8fHx8MTc2NzcyNzg2NXww&ixlib=rb-4.1.0&q=85"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50"></div>
        </div>
        <div className="relative container-custom h-full flex items-center">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
              Premium Tech,
              <br />
              <span className="text-accent">Trusted Partnership</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Experience the fusion of quality, authenticity, and exceptional service. Your journey to excellence starts here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button size="lg" className="btn-primary" data-testid="hero-shop-button">
                  Shop Now <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/categories">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white hover:bg-white/20 text-white" data-testid="hero-explore-button">
                  Explore Categories
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="section-padding bg-secondary/30" data-testid="categories-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Shop by Category</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover our curated selection of premium technology products
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                data-testid={`category-${category.id}`}
              >
                <Card className="card-product h-full">
                  <CardContent className="p-6 text-center">
                    <div className="h-20 flex items-center justify-center mb-4">
                      <div className="text-primary text-4xl">📱</div>
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{category.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding" data-testid="featured-products-section">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Featured Products</h2>
              <p className="text-muted-foreground">Handpicked for excellence</p>
            </div>
            <Link to="/products">
              <Button variant="ghost" data-testid="view-all-button">
                View All <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton h-56 sm:h-80 rounded-2xl"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {featuredProducts.map((product) => (
                <Link
                  key={product.product_id}
                  to={`/product/${product.product_id}`}
                  data-testid={`product-${product.product_id}`}
                >
                  <Card className="card-product">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbGFwdG9wJTIwd29ya3NwYWNlJTIwd2hpdGUlMjBkZXNrfGVufDB8fHx8MTc2NzcyNzg2NXww&ixlib=rb-4.1.0&q=85'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-3 sm:p-6">
                      <div className="hidden sm:flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} size={14} className="fill-accent text-accent" />
                        ))}
                        <span className="text-sm text-muted-foreground ml-2">(48)</span>
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-1 sm:mb-2 line-clamp-2">{product.name}</h3>
                      <p className="hidden sm:block text-muted-foreground text-sm mb-4 line-clamp-1">{hintText(product.description)}</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-base sm:text-2xl font-bold font-mono truncate">KES {product.price.toLocaleString()}</span>
                        <Button
                          size="sm"
                          className="btn-primary shrink-0 px-2.5 sm:px-4 text-xs sm:text-sm"
                          onClick={(e) => handleAddToCart(e, product.product_id)}
                          data-testid={`add-to-cart-${product.product_id}`}
                        >
                          <span className="hidden sm:inline">Add to Cart</span>
                          <span className="sm:hidden">Add</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section-padding bg-primary text-primary-foreground" data-testid="trust-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="text-4xl mb-4">✓</div>
              <h3 className="text-xl font-semibold mb-2">Authenticity Guaranteed</h3>
              <p className="text-primary-foreground/80">100% genuine products from authorized distributors</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-xl font-semibold mb-2">Fast Delivery</h3>
              <p className="text-primary-foreground/80">Same-day delivery available in Nairobi</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-semibold mb-2">Warranty & Support</h3>
              <p className="text-primary-foreground/80">Comprehensive warranty on all products</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="section-padding" data-testid="community-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join the Bamo Tech Sphere Community</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Stay updated with tech tips, product guides, and exclusive offers
            </p>
          </div>
          <div className="flex justify-center">
            <Link to="/community">
              <Button size="lg" className="btn-primary" data-testid="join-community-button">
                Explore Community <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;